import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { favouredRunway, runwayDesignator, runwayWind, type AerodromeDetail } from '@jupi/shared'
import { Title } from '@jupi/ui'
import { useAerodromes } from '../../api/aerodromes'
import { useAerodrome, useCurrentPlan, usePlanAircraft } from '../../api/plan'
import { CockpitView } from './CockpitView'
import { CW_COLORS, CrosswindDiagram } from './CrosswindDiagram'

// The runways to offer before an aerodrome's own are known (or when it has
// none loaded): Hyvinkää's, the club's home field. Nominal magnetic headings
// from the designators; type the AIP figure for anything else.
const EFHV_RUNWAYS = [
  { label: '04', heading: 40 },
  { label: '22', heading: 220 },
  { label: '12', heading: 120 },
  { label: '30', heading: 300 },
]

// Each end of each runway: its designator and magnetic heading.
function runwayPresets(runways: AerodromeDetail['runways']) {
  return runways.flatMap((r) => {
    const ends = r.designator.split('/')
    return r.headingMag ? ends.map((label, i) => ({ label, heading: r.headingMag![i]! })) : []
  })
}

const wrap360 = (n: number) => ((Math.round(n) % 360) + 360) % 360
const pad3 = (n: number) => String(wrap360(n)).padStart(3, '0')

type Editing = 'wind' | 'runway' | null

// Webplan's crosswind calculator: runway, wind and the plan's aircraft in;
// crosswind, head- or tailwind and the into-wind runway out, checked against
// the aircraft's demonstrated crosswind. The wind and runway are set by
// tapping them, which opens a numeric keypad on a phone.
export default function CrosswindPage() {
  const { t } = useTranslation()
  const [runwayHeading, setRunwayHeading] = useState(EFHV_RUNWAYS[0]!.heading)
  const [windDir, setWindDir] = useState(120)
  const [windSpeed, setWindSpeed] = useState(12)
  const [editing, setEditing] = useState<Editing>(null)
  const { data: aircraft = [] } = usePlanAircraft()
  const { data: current } = useCurrentPlan()
  // The aerodrome whose runways one tap sets (#81): the club's home field to
  // begin with, any aerodrome with details loaded after that.
  const [aerodromeCode, setAerodromeCode] = useState<string>('EFHV')
  const { data: aerodromes = [] } = useAerodromes()
  const { data: aerodrome } = useAerodrome(aerodromeCode)
  const runwayChoices = runwayPresets(aerodrome?.runways ?? [])
  const shownRunways = runwayChoices.length > 0 ? runwayChoices : aerodromeCode === 'EFHV' ? EFHV_RUNWAYS : []

  // Picking an aerodrome loads its first runway; the chips or the keypad
  // change it from there, so a typed heading survives until the next pick.
  const loadedCode = aerodrome?.code
  useEffect(() => {
    const first = runwayPresets(aerodrome?.runways ?? [])[0]
    if (first) setRunwayHeading(first.heading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedCode])

  // The aircraft is the one chosen on the flight plan.
  const registration = current?.plan.registration ?? null
  const planAircraft = aircraft.find((a) => a.registration === registration)
  const limitKt = planAircraft?.crosswindLimitKt ?? null
  const choice = favouredRunway(runwayHeading, windDir, windSpeed, limitKt)
  const onGiven = runwayWind(runwayHeading, windDir, windSpeed)
  const crosswind = choice.wind.crosswindKt
  const side = crosswind > 0.05 ? t('plan.crosswind.fromRight') : crosswind < -0.05 ? t('plan.crosswind.fromLeft') : ''

  return (
    <Box>
      <Title label={t('plan.crosswind.title')} />
      <Autocomplete
        size="small"
        sx={{ maxWidth: 360, mb: 1 }}
        options={aerodromes.map((a) => a.code)}
        value={aerodromeCode}
        onChange={(_, code) => code && setAerodromeCode(code)}
        disableClearable
        renderInput={(params) => <TextField {...params} label={t('plan.crosswind.aerodrome')} />}
        getOptionLabel={(code) => {
          const a = aerodromes.find((x) => x.code === code)
          return a?.name ? `${code} · ${a.name}` : code
        }}
      />
      {/* The answer, pinned under the shell's header on a phone so it
          stays in view while the wind is being set. */}
      <Box
        sx={{
          position: { xs: 'sticky', md: 'static' },
          top: { xs: 100, md: 'auto' },
          zIndex: 2,
          bgcolor: 'background.default',
          py: 1,
          mb: 1,
        }}
      >
        <Grid container spacing={1}>
          <Grid size={4}>
            <Metric
              label={t('plan.crosswind.crosswind')}
              hint={choice.overLimit ? t('plan.crosswind.overLimit') : side}
              value={`${Math.abs(crosswind).toFixed(1)} kt`}
              color={CW_COLORS.crosswind}
              danger={choice.overLimit === true}
            />
          </Grid>
          <Grid size={4}>
            <Metric
              label={onGiven.headwindKt < 0 ? t('plan.crosswind.tailwind') : t('plan.crosswind.headwind')}
              hint={`RWY ${runwayDesignator(runwayHeading)}`}
              value={`${Math.abs(onGiven.headwindKt).toFixed(1)} kt`}
              color="#1f9d4f"
              danger={onGiven.headwindKt < 0}
            />
          </Grid>
          <Grid size={4}>
            <Metric label={t('plan.crosswind.favoured')} value={`RWY ${choice.designator}`} color={CW_COLORS.intoWind} />
          </Grid>
        </Grid>
      </Box>

      {shownRunways.length > 0 ? (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 1, alignItems: 'center' }}>
          {shownRunways.map((r) => (
            <Chip
              key={r.label}
              label={`RWY ${r.label}`}
              color={r.heading === runwayHeading ? 'primary' : 'default'}
              variant={r.heading === runwayHeading ? 'filled' : 'outlined'}
              onClick={() => setRunwayHeading(r.heading)}
            />
          ))}
          <Chip
            label={t('plan.crosswind.customRunway')}
            icon={<Icon icon="mdi:pencil" />}
            variant={shownRunways.some((r) => r.heading === runwayHeading) ? 'outlined' : 'filled'}
            onClick={() => setEditing('runway')}
          />
        </Stack>
      ) : null}

      {registration === null ? (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" component={RouterLink} to="/plan">
              {t('plan.crosswind.openPlan')}
            </Button>
          }
        >
          {t('plan.crosswind.noPlanAircraft')}
        </Alert>
      ) : null}

      <Card>
        <CardContent>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
            {registration !== null ? (
              <Chip
                icon={<Icon icon="mdi:airplane" />}
                label={`${registration} · ${t('plan.crosswind.limit')}: ${limitKt !== null ? `${limitKt} kt` : t('plan.crosswind.noLimit')}`}
                color={choice.overLimit ? 'error' : 'default'}
                variant="outlined"
              />
            ) : null}
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="outlined"
              endIcon={<Icon icon="mdi:pencil" />}
              onClick={() => setEditing('wind')}
              sx={{ color: CW_COLORS.wind, borderColor: CW_COLORS.wind, '&:hover': { borderColor: CW_COLORS.wind, bgcolor: `${CW_COLORS.wind}20` } }}
            >
              {t('plan.crosswind.windShort')} {pad3(windDir)}° / {windSpeed} kt
            </Button>
            <Button variant="outlined" endIcon={<Icon icon="mdi:pencil" />} onClick={() => setEditing('runway')}>
              RWY {runwayDesignator(runwayHeading)} · {pad3(runwayHeading)}°
            </Button>
          </Stack>
          <Grid container spacing={2} sx={{ alignItems: 'center' }}>
            <Grid size={{ xs: 12, lg: 6 }}>
              {/* Tapping the rose is the other way in to the wind. */}
              <Box
                role="button"
                tabIndex={0}
                title={t('plan.crosswind.tapToEdit')}
                onClick={() => setEditing('wind')}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setEditing('wind')}
                sx={{ cursor: 'pointer', borderRadius: 1 }}
              >
                <CrosswindDiagram
                  runwayHdg={runwayHeading}
                  windDir={windDir}
                  windSpeed={windSpeed}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <CockpitView runwayHdg={runwayHeading} windDir={windDir} windSpeed={windSpeed} />
            </Grid>
          </Grid>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5, mt: 1.5, justifyContent: 'center' }}>
            {(
              [
                [CW_COLORS.wind, t('plan.crosswind.legendWind')],
                [CW_COLORS.crosswind, t('plan.crosswind.legendCrosswind')],
                [CW_COLORS.aircraft, t('plan.crosswind.legendAircraft')],
                [CW_COLORS.intoWind, t('plan.crosswind.legendIntoWind')],
              ] as const
            ).map(([color, label]) => (
              <Stack key={label} direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: color }} />
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <NumberDialog
        open={editing === 'wind'}
        title={t('plan.crosswind.editWind')}
        fields={[
          { key: 'dir', label: t('plan.crosswind.windDir'), value: windDir, max: 360, unit: '°', bearing: true },
          { key: 'speed', label: t('plan.crosswind.windSpeed'), value: windSpeed, max: 99, unit: 'kt' },
        ]}
        onClose={() => setEditing(null)}
        onSave={({ dir, speed }) => {
          setWindDir(dir!)
          setWindSpeed(speed!)
        }}
      />
      <NumberDialog
        open={editing === 'runway'}
        title={t('plan.crosswind.editRunway')}
        fields={[{ key: 'hdg', label: t('plan.crosswind.runway'), value: runwayHeading, max: 360, unit: '°', bearing: true }]}
        presets={shownRunways.map((r) => ({ key: 'hdg', label: r.label, value: r.heading }))}
        presetsLabel={t('plan.crosswind.runwaysOf', { code: aerodromeCode })}
        onClose={() => setEditing(null)}
        onSave={({ hdg }) => setRunwayHeading(hdg!)}
      />
    </Box>
  )
}

function Metric({
  label,
  hint,
  value,
  color,
  danger = false,
}: {
  label: string
  hint?: string
  value: string
  color: string
  danger?: boolean
}) {
  return (
    <Box
      sx={{
        px: { xs: 1, sm: 2 },
        py: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: danger ? 'error.main' : color,
        bgcolor: danger ? 'error.main' : `${color}20`,
        color: danger ? 'error.contrastText' : 'text.primary',
        height: '100%',
      }}
    >
      <Typography variant="caption" component="div" sx={{ opacity: 0.85, lineHeight: 1.3 }}>
        {label}
        {hint ? ` · ${hint}` : ''}
      </Typography>
      <Typography
        variant="h5"
        sx={{ fontFamily: 'monospace', lineHeight: 1.2, fontSize: { xs: '1.05rem', sm: '1.5rem' }, whiteSpace: 'nowrap' }}
      >
        {value}
      </Typography>
    </Box>
  )
}

interface NumberField {
  key: string
  label: string
  value: number
  max: number
  unit: string
  /** A bearing wraps 0-360 and shows three digits (040). */
  bearing?: boolean
}

// A small form of whole numbers. inputMode="numeric" brings up a plain
// number pad on a phone; Enter saves.
function NumberDialog({
  open,
  title,
  fields,
  presets = [],
  presetsLabel,
  onClose,
  onSave,
}: {
  open: boolean
  title: string
  fields: NumberField[]
  presets?: { key: string; label: string; value: number }[]
  presetsLabel?: string
  onClose: () => void
  onSave: (values: Record<string, number>) => void
}) {
  const { t } = useTranslation()
  const format = (f: NumberField, n: number) => (f.bearing ? pad3(n) : String(Math.round(n)))
  const [text, setText] = useState<Record<string, string>>({})

  const start = () => setText(Object.fromEntries(fields.map((f) => [f.key, format(f, f.value)])))
  const parsed = (f: NumberField) => {
    const raw = (text[f.key] ?? '').trim()
    const n = Number(raw)
    return raw === '' || !Number.isInteger(n) || n < 0 || n > f.max ? null : n
  }
  const valid = fields.every((f) => parsed(f) !== null)

  const save = () => {
    if (!valid) return
    onSave(Object.fromEntries(fields.map((f) => [f.key, f.bearing ? wrap360(parsed(f)!) : parsed(f)!])))
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" slotProps={{ transition: { onEnter: start } }}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          save()
        }}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {presets.length > 0 ? (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {presetsLabel}
                </Typography>
                <Stack direction="row" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                  {presets.map((p) => (
                    <Chip
                      key={p.label}
                      label={p.label}
                      variant="outlined"
                      onClick={() => setText((prev) => ({ ...prev, [p.key]: pad3(p.value) }))}
                      sx={{ minWidth: 56, height: 40 }}
                    />
                  ))}
                </Stack>
              </Box>
            ) : null}
            {fields.map((f, i) => (
              <TextField
                key={f.key}
                fullWidth
                autoFocus={i === 0}
                label={f.label}
                value={text[f.key] ?? ''}
                error={text[f.key] !== undefined && parsed(f) === null}
                onChange={(e) => setText((prev) => ({ ...prev, [f.key]: e.target.value }))}
                onFocus={(e) => e.target.select()}
                slotProps={{
                  htmlInput: { inputMode: 'numeric', pattern: '[0-9]*', enterKeyHint: i === fields.length - 1 ? 'done' : 'next' },
                  input: { endAdornment: <InputAdornment position="end">{f.unit}</InputAdornment> },
                }}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={!valid}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
